import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'
import { 
  Download, FileText, Calendar, CheckCircle, 
  AlertCircle, Users, MessageSquare, Lightbulb, Trash2
} from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'

export function ReportsSection() {
  const [generating, setGenerating] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')

  const downloadReport = async (reportType: string) => {
    setGenerating(reportType)
    try {
      const response = await fetch(
        `${API_BASE_URL}admin/report/${reportType}?range=${dateRange}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        }
      )
      
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `aira_${reportType}_report_${dateRange}_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else {
        alert('Failed to generate report. Please try again.')
      }
    } catch (error) {
      console.error('Error downloading report:', error)
      alert('Failed to generate report. Please check your connection.')
    } finally {
      setGenerating(null)
    }
  }

  const reportTypes = [
    {
      id: 'users',
      title: 'Users Report',
      description: 'Complete list of all registered users with their activity metrics',
      icon: Users,
      color: 'indigo'
    },
    {
      id: 'chats',
      title: 'Chats Report',
      description: 'All chat sessions with timestamps and user information',
      icon: MessageSquare,
      color: 'green'
    },
    {
      id: 'messages',
      title: 'Messages Report',
      description: 'Detailed message logs with content and metadata',
      icon: FileText,
      color: 'blue'
    },
    {
      id: 'suggestions',
      title: 'Suggestions Report',
      description: 'User feedback and suggestions for improvement',
      icon: Lightbulb,
      color: 'yellow'
    },
    {
      id: 'analytics',
      title: 'Analytics Report',
      description: 'Comprehensive analytics with charts and insights',
      icon: CheckCircle,
      color: 'purple'
    },
    {
      id: 'deleted-sessions',
      title: 'Deleted Sessions Report',
      description: 'Chat sessions that have been deleted by users',
      icon: Trash2,
      color: 'orange'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-indigo-600" />
            Report Settings
          </CardTitle>
          <CardDescription>Select date range for reports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Label>Date Range</Label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setDateRange('7d')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === '7d'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Last 7 Days
              </button>
              <button
                onClick={() => setDateRange('30d')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === '30d'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Last 30 Days
              </button>
              <button
                onClick={() => setDateRange('90d')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === '90d'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Last 90 Days
              </button>
              <button
                onClick={() => setDateRange('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                All Time
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reportTypes.map((report) => (
          <Card key={report.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-${report.color}-100 dark:bg-${report.color}-900`}>
                    <report.icon className={`h-6 w-6 text-${report.color}-600 dark:text-${report.color}-400`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{report.title}</CardTitle>
                  </div>
                </div>
              </div>
              <CardDescription className="mt-2">
                {report.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => downloadReport(report.id)}
                disabled={generating === report.id}
                className="w-full"
                variant="outline"
              >
                {generating === report.id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
            <AlertCircle className="h-5 w-5" />
            Report Information
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
          <p>• Reports are generated in CSV format for easy analysis in Excel or Google Sheets</p>
          <p>• All reports include data within the selected date range</p>
          <p>• User data is exported securely and should be handled according to privacy policies</p>
          <p>• Reports can be used for analytics, auditing, and business intelligence</p>
        </CardContent>
      </Card>
    </div>
  )
}

